/**
 * RTWO Cryptography & Identity Engine — Event Publishing Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { CryptoEvent, CryptoEventType } from '../domain-models';
import type { CryptoEventHandler, ICryptoEventPublishingService } from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import { cryptoEventBus } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

let eventCounter = 0;

function generateEventId(): string {
  eventCounter += 1;
  return `cryp-evt-${eventCounter}`;
}

export class CryptoEventPublishingService
  extends BaseCryptoService
  implements ICryptoEventPublishingService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.EVENT_PUBLISHING;
  readonly serviceName = 'Cryptography Event Publishing Service';
  readonly description = 'Publishes cryptography lifecycle events to the Core Engine event bus.';
  readonly layer = 'R3' as const;
  readonly integrations = ['R3 Core Orchestrator', 'System Registry'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = false;

  private readonly handlers = new Map<CryptoEventType, CryptoEventHandler[]>();

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories
  ) {
    super(context);
  }

  publish(eventType: CryptoEventType, entityId: string, payload: Record<string, unknown>): void {
    const event: CryptoEvent = {
      eventId: generateEventId(),
      eventType,
      entityId,
      timestamp: Date.now(),
      payload,
    };

    this.repositories.eventHistory.add(event);
    this.context.eventBus.emit(eventType, entityId, payload);

    const handlers = this.handlers.get(eventType) ?? [];
    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        console.error(`[CryptoEventPublishingService] Handler error for ${eventType}:`, error);
      }
    }
  }

  subscribe(eventType: CryptoEventType, handler: CryptoEventHandler): () => void {
    const existing = this.handlers.get(eventType) ?? [];
    this.handlers.set(eventType, [...existing, handler]);

    const busUnsubscribe = cryptoEventBus.subscribe(eventType, (busEvent) => {
      handler({
        eventId: busEvent.id,
        eventType: busEvent.type,
        entityId: busEvent.entityId,
        timestamp: busEvent.timestamp,
        payload: busEvent.payload as Record<string, unknown>,
      });
    });

    return () => {
      const current = this.handlers.get(eventType) ?? [];
      this.handlers.set(
        eventType,
        current.filter((entry) => entry !== handler)
      );
      busUnsubscribe();
    };
  }

  getRecentEvents(limit = 20): CryptoEvent[] {
    return this.repositories.eventHistory.findRecent(limit);
  }
}
